import http from "../../utils/http";
import { permissions } from "../../utils/roles";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import {
  clearQuestionSelection,
  resetQuestionState,
  setOptionGroups,
  setQuestionLoader,
  setQuestionnaireDetails,
  setQuestionSubmitting,
  toggleFormBuilder,
} from "../reducers/questionSlice";
import { AppThunk } from "../store";

export const loadQuestionDetails =
  (
    studyId: string | undefined,
    formId: string,
    type: "platform" | "study",
    previewMode?: boolean,
    lang?: string
  ): AppThunk =>
  async (dispatch, getState) => {
    try {
      if (!formId || (type === "study" && !studyId)) {
        dispatch(resetQuestionState());
        return;
      }

      dispatch(setQuestionLoader(true));
      const prefix =
        type === "platform" ? "/admin/registry/" : `/study/${studyId}/`;
      let data: any;
      if (lang && lang !== "en") {
        data = await http.get(`${prefix}forms/${formId}?lang=${lang}`);
      } else {
        data = await http.get(`${prefix}forms/${formId}`);
      }

      if (data?.data && data?.data?.data) {
        const formStatus = data?.data?.data?.status || "draft";
        const studyStatus =
          type === "platform" ? true : getState().study.status === "not_live";
        const isScreener =
          data?.data?.data?.phaseForm?.phase?.category === "screening";
        const hasEditAccess =
          type === "platform"
            ? true
            : getState().study.studyPermissions.includes(
                permissions.studyDesigner
              );
        const editPermission = previewMode
          ? false
          : isScreener
          ? formStatus === "draft" && studyStatus && hasEditAccess
          : studyStatus && hasEditAccess;

        // Sort Questions
        let questions = data.data.data.questions
          ?.slice()
          .sort(
            (a: { position: number }, b: { position: number }) =>
              a.position - b.position
          );

        questions = questions.map((q: any) => {
          if (q.choices) {
            q.choices = q.choices.sort(
              (a: { position: number }, b: { position: number }) =>
                a.position - b.position
            );
          }
          if (q?.validations) {
            q.validations = q.validations.sort(
              (a: { position: number }, b: { position: number }) =>
                a.position - b.position
            );
          }
          q.labelMarkdown = q.label.replace(/(?:\r\n|\r|\n)/g, "<br>");
          return {
            ...q,
          };
        });

        dispatch(
          setQuestionnaireDetails({
            questionName: data?.data?.data?.name,
            error: "",
            loading: false,
            editable: editPermission,
            formSubmitting: false,
            formLevel: type,
            toggleForm: false,

            status: formStatus,
            formDirty: false,

            showPropModal: false,
            modalQuestion: null,
            modalIsEdit: false,

            isBasicError: "",
            isChoicesError: "",
            isRepeatedConfigError: "",
            isGridConfigError: "",
            isValidationsError: "",
            isDependencyError: "",
            isTemplateError: "",
            isLogicJumpsError: "",
            isAddressError: "",

            questions: questions,
            formId: formId,
            studyId: studyId || "",
            revision: data?.data?.data?.revision,
            formCategory: data?.data?.data?.phaseForm?.phase?.category,
            // lang: lang,

            selectedQuestionIds: [],
            questionValidations: {},
          })
        );
      }
    } catch (err) {
      dispatch(setQuestionSubmitting(false));
      dispatch(setQuestionLoader(false));
      errorToastMessage(err as Error);
    }
  };

export const loadOptionGroups =
  (studyId: string): AppThunk =>
  async (dispatch, getState) => {
    const editPermission =
      getState().study.studyPermissions.includes(permissions.studyDesigner) &&
      getState().study.status === "not_live";

    if (editPermission) {
      try {
        const choiceRes = await http.get(`/study/${studyId}/option-groups`);
        let choiceData = (choiceRes.data?.data || [])?.map((item: any) => ({
          id: item?.id,
          name: item?.name || "-",
          options: item?.options || [],
        }));
        dispatch(setOptionGroups({ options: choiceData }));
      } catch (err) {}
    }
  };

export const saveQuestion =
  (isUpdate: boolean): AppThunk =>
  async (dispatch, getState) => {
    const { formId, studyId, revision, modalQuestion, formLevel } =
      getState().question;

    //Add Position to Validation
    const quesValidations = modalQuestion.validations.map(
      (v: any, i: number) => {
        const {
          id,
          type,
          operator,
          numberValue,
          textValue,
          questionChoiceId,
          message,
          autoGenerateQuery,
        } = v;
        return {
          id,
          type,
          operator,
          numberValue,
          textValue,
          questionChoiceId,
          message,
          position: i + 1,
          autoGenerateQuery,
        };
      }
    );

    const quesLogicJumps = modalQuestion.logicJumps.map((v: any, i: number) => {
      const {
        id,
        operator,
        numberValue,
        textValue,
        questionChoiceId,
        nextQuestionId,
      } = v;
      return {
        id,
        operator,
        numberValue,
        textValue,
        questionChoiceId,
        nextQuestionId,
        position: i + 1,
      };
    });

    const choices = (modalQuestion.choices || []).map((choice: any) => {
      const { id, label, position, value, isOther, ref, imageUrl } = choice;
      return {
        id,
        label,
        position,
        value,
        isOther,
        ref,
        imageUrl,
      };
    });

    const dependency = modalQuestion?.dependency
      ? {
          parentFormId: modalQuestion.dependency?.parentFormId,
          parentQuestionId: modalQuestion.dependency?.parentQuestionId,
          operator: modalQuestion.dependency?.operator,
          numberValue: modalQuestion.dependency?.numberValue,
          textValue: modalQuestion.dependency?.textValue,
          questionChoiceId: modalQuestion.dependency?.questionChoiceId,
        }
      : undefined;

    const ques = {
      ...modalQuestion,
      dependency,
      choices,
      validations: quesValidations,
      logicJumps: quesLogicJumps,
    };

    const body = {
      revision,
      question: ques,
    };

    try {
      dispatch(setQuestionLoader(true));
      dispatch(setQuestionSubmitting(true));
      const prefix =
        formLevel === "platform" ? "/admin/registry/" : `/study/${studyId}/`;
      if (isUpdate) {
        const { data } = await http.patch(
          `${prefix}forms/${formId}/question/${modalQuestion.id}`,
          body
        );

        toastMessage("success", data.message);
        dispatch(toggleFormBuilder());
      } else {
        const { data } = await http.post(
          `${prefix}forms/${formId}/question`,
          body
        );

        toastMessage("success", data.message);
        dispatch(toggleFormBuilder());
      }
    } catch (err) {
      dispatch(setQuestionSubmitting(false));
      dispatch(setQuestionLoader(false));
      errorToastMessage(err as Error);
    }
  };

export const importQuestion =
  (file: File): AppThunk =>
  async (dispatch, getState) => {
    const { formId, studyId, formLevel } = getState().question;
    try {
      dispatch(setQuestionLoader(true));
      dispatch(setQuestionSubmitting(true));
      const formData = new FormData();
      formData.append("file", file);

      const prefix =
        formLevel === "platform" ? "/admin/registry/" : `/study/${studyId}/`;

      // Do not set Content-Type manually — axios 1.x must auto-detect FormData
      // and let the browser add the multipart boundary.
      let res = await http.post(`${prefix}forms/${formId}/import`, formData, {
        responseType: "blob",
      });
      const contentType = res.headers["content-type"] || "";
      if (contentType.includes("text/csv")) {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        toastMessage("warning", "Import errors found. Please fix and re-upload.");
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "import_errors.csv");
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        dispatch(setQuestionSubmitting(false));
        dispatch(setQuestionLoader(false));
      } else {
        const json = JSON.parse(await res.data.text());
        toastMessage("success", json?.message || "Questions imported successfully");
        dispatch(toggleFormBuilder());
      }
    } catch (err:any) {
      try {
        const text = await err.response.data.text();
        const errorData = JSON.parse(text);
        errorToastMessage(errorData || "Upload failed");
      } catch (e) {
        errorToastMessage(err as Error);
      } finally {
        dispatch(setQuestionSubmitting(false));
        dispatch(setQuestionLoader(false));
      }
    }
  };

export const bulkSetDependencies =
  (payload: {
    questionIds: string[];
    dependency: {
      parentFormId: string;
      parentQuestionId: string;
      operator: string;
      textValue?: string;
      numberValue?: number;
      questionChoiceId?: string;
    };
  }): AppThunk =>
  async (dispatch, getState) => {
    const { formId, studyId, revision, formLevel } = getState().question;

    try {
      dispatch(setQuestionSubmitting(true));
      const prefix =
        formLevel === "platform" ? "/admin/registry/" : `/study/${studyId}/`;

      const { data } = await http.patch(
        `${prefix}forms/${formId}/dependencies/bulk`,
        {
          revision,
          questionIds: payload.questionIds,
          dependency: payload.dependency,
        }
      );

      toastMessage("success", data.message);
      dispatch(toggleFormBuilder());
    } catch (err) {
      errorToastMessage(err as Error);
    } finally {
      dispatch(setQuestionSubmitting(false));
    }
  };

export const deleteQuestion =
  (questionId: string): AppThunk =>
  async (dispatch, getState) => {
    const { formId, studyId, revision, formLevel } = getState().question;

    try {
      dispatch(setQuestionSubmitting(true));
      const prefix =
        formLevel === "platform" ? "/admin/registry/" : `/study/${studyId}/`;

      const { data } = await http.delete(
        `${prefix}forms/${formId}/question/${questionId}/${revision}`
      );
      if (data) {
        toastMessage("success", data.message);
        dispatch(toggleFormBuilder());
      }
    } catch (err) {
      dispatch(setQuestionSubmitting(false));
      errorToastMessage(err as Error);
    }
  };

export const bulkDeleteQuestions =
  (questionIds: string[]): AppThunk =>
  async (dispatch, getState) => {
    const { formId, studyId, revision, formLevel } = getState().question;

    try {
      dispatch(setQuestionSubmitting(true));
      const prefix =
        formLevel === "platform" ? "/admin/registry/" : `/study/${studyId}/`;

      const { data } = await http.delete(`${prefix}forms/${formId}/questions`, {
        data: { questionIds, revision },
      });
      if (data) {
        toastMessage("success", data.message);
        dispatch(clearQuestionSelection());
        dispatch(toggleFormBuilder());
      }
    } catch (err) {
      dispatch(setQuestionSubmitting(false));
      errorToastMessage(err as Error);
    }
  };
